import { AlertTriangleIcon } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import BudgetRow from "./BudgetRow";
import SpentRow from "./SpentRow";

import { Badge } from "@/components/ui/v2/badge";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/v2/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";
import { cn } from "@/lib/utils/cn";
import { JobboardCampaignType } from "@/types/response/CampaignJobBoardMonthType";
import { formatCurrency } from "@/utils/valueFormat";

interface MonthCardProps {
  monthString: string;
  year: number;
  month: number;
  existingData: JobboardCampaignType;
  editingBudgetMonth: string | null;
  editingSpentMonth: string | null;
  editBudgetValue: string;
  editSpentValue: string;
  isLoadingBudget: boolean;
  isLoadingSpent: boolean;
  canEditSpent: boolean;
  canCreateSpent: boolean;
  canViewSpent: boolean;
  isFutureMonth: boolean;
  isPastMonth: boolean;
  isCurrentMonth: boolean;
  minimumBudget?: number;
  vacancyCount?: number;
  onEditBudget: () => void;
  onSaveBudget: () => void;
  onCancelBudget: () => void;
  onEditSpent: () => void;
  onSaveSpent: () => void;
  onCancelSpent: () => void;
  onBudgetValueChange: (value: string) => void;
  onSpentValueChange: (value: string) => void;
  getNumberValue: (value: string) => string;
  startDate?: string;
}

function MonthCard({
  monthString,
  year,
  month,
  existingData,
  editingBudgetMonth,
  editingSpentMonth,
  editBudgetValue,
  editSpentValue,
  isLoadingBudget,
  isLoadingSpent,
  canEditSpent,
  canCreateSpent,
  canViewSpent,
  isFutureMonth,
  isPastMonth,
  isCurrentMonth,
  minimumBudget,
  vacancyCount,
  onEditBudget,
  onSaveBudget,
  onCancelBudget,
  onEditSpent,
  onSaveSpent,
  onCancelSpent,
  onBudgetValueChange,
  onSpentValueChange,
  getNumberValue,
  startDate,
}: MonthCardProps) {
  const { t, i18n } = useTranslation();
  const isEditingBudget = editingBudgetMonth === monthString;
  const isEditingSpent = editingSpentMonth === monthString;
  const hasBudget = true;

  const formatMonth = (year: number, month: number) => {
    const date = new Date(year, month - 1, 1);
    return new Intl.DateTimeFormat(i18n.language || undefined, {
      month: "long",
    }).format(date);
  };
  // Determine card state
  const isInvoiced = existingData.isInvoiced;
  const isProrated = existingData.monthlyBudget !== null;
  // Compare the monthly budget, not the prorated allocation: proration exists precisely so a partial
  // first month can run below the minimum, so comparing the prorated amount flags every prorated month.
  const budgetForMinimumCheck = existingData.monthlyBudget ?? existingData.budget;
  const isBelowMinimum = isCurrentMonth && minimumBudget != null && minimumBudget > 0 && budgetForMinimumCheck < minimumBudget;

  return (
    <motion.div
      layout
      data-slot="panel"
      transition={{ duration: 0.1, ease: "easeInOut" }}
      className={cn(
        "bg-card text-card-foreground flex flex-col gap-6 rounded-lg border border-foreground/25 py-6 shadow-sm transition-colors group relative overflow-hidden"
      )}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base font-semibold">
            {formatMonth(year, month)}
          </CardTitle>
          <div className="flex gap-1.5">
            {isBelowMinimum && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div tabIndex={0} className="cursor-default">
                    <Badge variant="warning" className="gap-1">
                      <AlertTriangleIcon className="size-3" />
                      {t("admin.campaigns.jobBoard.belowMinimum", {
                        defaultValue: "Below minimum",
                      })}
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {t("admin.campaigns.jobBoard.belowMinimumTooltip", {
                    defaultValue:
                      "Minimum requirement is {{minimum}} for {{count}} vacancies in this segment.",
                    minimum: formatCurrency(minimumBudget!),
                    count: vacancyCount ?? 0,
                  })}
                </TooltipContent>
              </Tooltip>
            )}
            {isProrated && (
              <Badge variant="secondary">
                {t("common.prorated")}
              </Badge>
            )}
            {isInvoiced && (
              <Badge variant="success">{t("common.invoiced")}</Badge>
            )}
            {isFutureMonth && !isInvoiced && (
              <Badge variant="info">{t("common.upcoming")}</Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <motion.div layout transition={{ duration: 0.1, ease: "easeInOut" }}>
        <CardContent className="flex flex-col gap-4">
          <BudgetRow
            existingData={existingData}
            isEditing={isEditingBudget}
            isEditingSpent={isEditingSpent}
            editValue={editBudgetValue}
            isLoading={isLoadingBudget}
            isNewMonth={false}
            isPastMonth={isPastMonth}
            isCurrentMonth={isCurrentMonth}
            minimumBudget={minimumBudget}
            onEdit={onEditBudget}
            onSave={onSaveBudget}
            onCancel={onCancelBudget}
            onValueChange={onBudgetValueChange}
            getNumberValue={getNumberValue}
            startDate={startDate}
          />
          {canViewSpent && (
            <SpentRow
              existingData={existingData}
              isEditing={isEditingSpent}
              isEditingBudget={isEditingBudget}
              editValue={editSpentValue}
              isLoading={isLoadingSpent}
              hasBudget={hasBudget}
              canEditSpent={canEditSpent}
              canCreateSpent={canCreateSpent}
              isFutureMonth={isFutureMonth}
              onEdit={onEditSpent}
              onSave={onSaveSpent}
              onCancel={onCancelSpent}
              onValueChange={onSpentValueChange}
              getNumberValue={getNumberValue}
            />
          )}
        </CardContent>
      </motion.div>
    </motion.div>
  );
}

export default MonthCard;
